/* Formalisation — official routes to register / apply, only after evidence and eligibility checks. */
import React from 'react';
import { Landmark, ClipboardCheck } from 'lucide-react';
import { StatusChip, SourceLink, orSource, CHECK_SOURCE } from './ownpathUi';

const FORMAL_TYPES = /registration|recognition|licen|approvals|single-window|guidance/i;

export default function Formalisation({ data }) {
  const route = data?.stateRoute || {};
  const formal = (data?.nationalCommon || []).filter((s) => FORMAL_TYPES.test(`${s.type} ${s.title}`));
  const checks = Array.from(new Set((data?.opportunityCards || []).flatMap((c) => c.mandatoryChecks || []))).slice(0, 8);
  return (
    <div className="space-y-4" data-testid="v20-formalisation">
      <div className="rounded-2xl border-2 border-gray-200 bg-white p-4" data-testid="v20-state-route">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Landmark size={16} className="text-rose-700" />
          <StatusChip colour="blue" label={(route.verificationStatus || 'ACTIVE_OFFICIAL_PORTAL_VERIFY_CURRENT_INTAKE').replace(/_/g, ' ').toLowerCase()} />
        </div>
        <h4 className="font-extrabold text-gray-900">{orSource(route.title)}</h4>
        <p className="text-xs text-gray-700 mt-0.5">{orSource(route.policyOrProgramme)} · verified {orSource(route.verifiedAt)}</p>
        {route.suitabilityWarning && <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-2">{route.suitabilityWarning}</p>}
        <div className="flex flex-wrap gap-2 mt-3">
          <SourceLink href={route.officialUrl} label={CHECK_SOURCE} testid="v20-state-route-cta" />
          {data?.startupIndiaPolicyReference?.officialUrl && <SourceLink href={data.startupIndiaPolicyReference.officialUrl} label="Startup India state policy (reference only)" className="!bg-gray-600 hover:!bg-gray-700" testid="v20-startup-india-ref" />}
        </div>
        {data?.laneOrder?.length > 0 && <p className="text-[11px] text-gray-500 mt-2">Route order: {data.laneOrder.join(' → ')}</p>}
      </div>
      {formal.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Official registration & application routes (free on official portals)</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {formal.map((s) => (
              <div key={s.id} className="rounded-xl border border-gray-200 bg-white p-3" data-testid={`v20-formal-${s.id}`}>
                <div className="flex items-center justify-between gap-2"><StatusChip colour={s.statusColour} label={s.statusLabel} /><span className="text-[10px] text-gray-500">{s.lastVerified}</span></div>
                <h5 className="font-bold text-gray-900 text-sm mt-1.5">{s.title}</h5>
                <p className="text-xs text-gray-700 mt-0.5">{orSource(s.purpose)}</p>
                {s.warning && <p className="text-[11px] text-amber-800 mt-1">{s.warning}</p>}
                <div className="mt-2"><SourceLink href={s.primaryUrl} testid={`v20-formal-${s.id}-cta`} /></div>
              </div>
            ))}
          </div>
        </div>
      )}
      {checks.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2 inline-flex items-center gap-1"><ClipboardCheck size={12} /> Before you apply or sell — checks from this state's cards</p>
          <ul className="list-disc pl-4 space-y-1 text-xs text-gray-800">{checks.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      <p className="text-xs text-gray-600">Formalise and apply only after demand, safety and eligibility evidence. Selecting an idea never makes you eligible for a scheme.</p>
    </div>
  );
}
